/* eslint-disable no-restricted-syntax */
/* globals paper */

import Vue from 'vue';
import VirtualScroll from 'virtual-scroll';
import { gsap, Elastic } from 'gsap/all';
import { disableBodyScroll } from 'body-scroll-lock';

import Line from './InverseLine';

Vue.component('liquid-header-component', {
    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            scrolledDown: false,
        };
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        // Prepare element class changes.
        this.content = document.querySelector('.content');
        this.reversed = true;
        this.onHeaderScrollDown = () => {
            if (this.scroller) {
                this.scroller.off(this.logScroll);
                this.scroller.destroy();
                this.scroller = null;
            }

            window.scrollData = {
                y: 0,
            };

            this.scrolledDown = true;

            this.content.classList.add('content--scrolled');
            document.body.classList.remove('header--dark');
            gsap.to('.--return .project__header-scroll-text', {
                autoAlpha: 1,
                duration: 0.5,
                delay: 1,
            });
        };

        this.onSnapBack = () => {
            if (this.scroller) {
                this.scroller.off(this.logScroll);
                this.scroller.destroy();
                this.scroller = null;
            }

            window.scrollData = {
                y: 0,
            };
        };

        // Listen for down or up events.
        EventBus.$on('header-scroll-down', this.onHeaderScrollDown);
        EventBus.$on('snap-back', this.onSnapBack);
        EventBus.$on('return-to-video', this.reverse);
        EventBus.$on('snap-back-complete', this.trackScroll);

        window.scrollTo(0, 0);
        window.requestAnimationFrame(() => {
            disableBodyScroll(document.querySelector('.header'));
        });

        // Let's go!
        EventBus.$once('page-transition-enter-complete', this.init);
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        init() {
            disableBodyScroll(document.querySelector('.header'));

            // get a reference to the canvas element
            const canvas = this.$el.querySelector('#project-header-canvas');

            // init paper scope
            this.scope = new paper.PaperScope();
            this.paper = this.scope.setup(canvas);

            const { view } = this.paper;

            let vw = view.bounds.width;
            let vh = view.bounds.height;

            let lines = [];
            const lineColor = 'transparent';
            const lineWidth = 0;

            window.showPoints = true;
            window.showMoire = true;
            window.yAxisLocked = false;
            window.xAxisLocked = true;

            const pathSegments = 8;

            const snapDist = 225;
            window.repelDist = 125;
            window.snapDistSq = snapDist ** 2;
            // eslint-disable-next-line no-undef
            window.repelDistSq = repelDist ** 2;

            window.duration = 6;
            window.easePeriod = 0.7;
            window.easeStrength = 1;
            // eslint-disable-next-line no-undef
            window.elasticEase = Elastic.easeOut.config(easeStrength, easePeriod);

            const center = new paper.Point(vw / 2, vh / 2);
            const mouse = { delta: new paper.Point(0, 0), point: center };
            window.mouse = mouse;

            const pointShape = new paper.Path.Circle({
                center: [0, 0],
                radius: 4,
                fillColor: 'transparent',
                strokeColor: 'transparent',
            });

            pointShape.fillColor.alpha = 0;

            const pointSymbol = new paper.Symbol(pointShape.rasterize());
            pointSymbol.definition.visible = false;
            pointShape.remove();

            let lineRaster = null;
            let lineSymbol = null;
            const lineShape = new paper.Path({
                segments: [[0, vh], [vw, vh]],
                strokeColor: lineColor,
                strokeWidth: lineWidth,
            });

            //
            // RASTERIZE LINE
            // ========================================================================
            function rasterizeLine() {
                if (lineRaster) {
                    lineRaster.remove();
                }

                lineShape.visible = true;
                lineShape.segments = [[0, vh], [vw, vh]];
                lineRaster = lineShape.rasterize();
                lineSymbol = new paper.Symbol(lineRaster);
                lineShape.visible = false;
            }

            function init() {
                vw = view.bounds.width;
                vh = view.bounds.height;

                for (const line of lines) {
                    line.destroy();
                }

                lines = [];
                rasterizeLine();
                // eslint-disable-next-line no-undef
                lines.push(new Line(0, vw / 2, lineSymbol, vw, vh, showMoire, pathSegments));
            }

            let render = true;

            function update() {
                for (const line of lines) {
                    line.update();
                }

                if (render) {
                    window.requestAnimationFrame(update);
                }
            }

            this.end = () => {
                this.reversed = false;
                render = false;
            };

            this.start = () => {
                render = true;
                window.requestAnimationFrame(update);
            };

            const resize = () => {
                render = true;
                window.requestAnimationFrame(() => {
                    update();
                    render = false;
                });
            };

            window.requestAnimationFrame(update);

            // eslint-disable-next-line no-undef
            this.debouncedResize = _.debounce(resize, 30);
            init();

            window.addEventListener('resize', this.debouncedResize);

            EventBus.$on('header-scroll-down-complete', this.end);
            this.trackScroll();
        },

        reverse() {
            if (this.reversed) {
                return;
            }

            // Control points are listening to the same event as this method.
            this.start();
            disableBodyScroll(document.querySelector('.header'));
            document.body.classList.add('header--dark');

            EventBus.$once('return-to-video-complete', () => {
                this.trackScroll();
                this.reversed = true;
            });
        },

        trackScroll() {
            this.scrolledDown = false;
            this.scroller = new VirtualScroll({
                el: this.$el,
                useKeyboard: false,
                touchMultiplier: 8,
                mouseMultiplier: 3,
            });

            let delayedCall;
            this.logScroll = (event) => {
                window.scrollData = {
                    y: event.y,
                    updated: new Date(),
                };

                delayedCall = gsap.delayedCall(0.8, () => {
                    delayedCall.kill();
                    const elapsed = new Date() - window.scrollData.updated;
                    const seconds = Math.round(elapsed / 1000);

                    if (seconds > 0.3 && !this.scrolledDown) {
                        EventBus.$emit('scroll-stopped');
                    }
                });
            };

            this.scroller.on(this.logScroll);
        },

        onDiscoverClick() {
            EventBus.$emit('snapTrough');
        },
    },

    beforeDestroy() {
        // Listen for down or up events.
        EventBus.$off('header-scroll-down', this.onHeaderScrollDown);
        EventBus.$off('snap-back', this.onSnapBack);
        EventBus.$off('return-to-video', this.reverse);
        EventBus.$off('snap-back-complete', this.trackScroll);
        EventBus.$off('header-scroll-down-complete', this.end);

        window.removeEventListener('resize', this.debouncedResize);

        if (this.scroller) {
            this.scroller.off(this.logScroll);
            this.scroller.destroy();
        }

        this.end();

        window.scrollData = null;
    },
});
